import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ACCENT = '#f59e0b';
const TOTAL_ROUNDS = 5;

const fmt = (s) => {
  if (s < 60) return `${s.toFixed(2)}s`;
  const m = Math.floor(s / 60);
  const r = (s % 60).toFixed(2);
  return `${m}m ${r}s`;
};

const PtsChip = ({ pts }) => {
  const color = pts === 100 ? '#22c55e' : pts >= 80 ? '#86efac' : pts >= 60 ? '#f59e0b' : pts >= 30 ? '#fb923c' : '#ef4444';
  return (
    <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ background: color + '22', color }}>
      +{pts} pts
    </span>
  );
};

const initSession = () => ({ key: null, raceName: '', rounds: [], guesses: [], input: '' });

const LeGap = () => {
  const { user, refreshUser } = useAuth();
  const [phase, setPhase]       = useState('loading');
  const [session, setSession]   = useState(initSession());
  const [result, setResult]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionXp, setSessionXp]   = useState(0);
  const [raceCount, setRaceCount]   = useState(0);
  const seenKeys = useRef([]);
  const hasInitialized = useRef(false);

  const loadRace = useCallback(async () => {
    if (!user) { setPhase('error'); return; }
    setPhase('loading');
    try {
      const exclude = seenKeys.current.join(',');
      const r = await api.get(`/game/gap/today${exclude ? `?exclude=${exclude}` : ''}`);
      seenKeys.current = [...seenKeys.current, r.data.key];
      setSession({ ...initSession(), key: r.data.key, raceName: r.data.raceName, rounds: r.data.rounds });
      setResult(null);
      setPhase('playing');
    } catch { setPhase('error'); }
  }, [user]);

  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      loadRace();
    }
  }, [user, loadRace]);

  const currentRound = session.guesses.length;
  const isLastRound  = currentRound === TOTAL_ROUNDS - 1;
  const current      = session.rounds[currentRound];

  const validateInput = () => {
    const v = parseFloat(session.input.replace(',', '.'));
    return isNaN(v) || v < 0 ? null : v;
  };

  const handleNext = async () => {
    const val = validateInput();
    if (val === null) return;
    const newGuesses = [...session.guesses, val];
    setSession(p => ({ ...p, guesses: newGuesses, input: '' }));

    if (newGuesses.length === TOTAL_ROUNDS) {
      setSubmitting(true);
      try {
        const r = await api.post('/game/gap/submit', { key: session.key, guesses: newGuesses });
        setSessionXp(p => p + r.data.xpEarned);
        setRaceCount(p => p + 1);
        setResult(r.data);
        setPhase('done');
        if (r.data.xpEarned > 0) refreshUser();
      } catch { setPhase('error'); }
      finally { setSubmitting(false); }
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleNext(); };

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-zinc-400">Chargement de la course…</p>
    </div>
  );

  if (phase === 'error') return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-zinc-400">Erreur lors du chargement.</p>
      <Link to="/box-box" className="btn-secondary">← Box Box Hub</Link>
    </div>
  );

  // ── RESULTS ────────────────────────────────────────────────────────────────
  if (phase === 'done' && result) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <p className="text-6xl mb-3">{result.score === 500 ? '🏆' : result.score >= 350 ? '🔥' : result.score >= 200 ? '👍' : '💀'}</p>
          <h1 className="text-3xl font-black text-white mb-1">{result.score}/500 pts</h1>
          <p className="text-zinc-400 text-sm mb-3">{result.raceName}</p>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl mb-2"
            style={{ background: ACCENT + '18', border: `1px solid ${ACCENT}44` }}>
            <span className="font-black text-xl" style={{ color: ACCENT }}>+{result.xpEarned} XP</span>
          </div>
          {raceCount > 1 && (
            <p className="text-zinc-500 text-xs mt-1">Session : <span className="text-white font-bold">+{sessionXp} XP</span> en {raceCount} courses</p>
          )}
          {result.levelInfo?.leveledUp && (
            <p className="text-yellow-400 font-black mt-2">🎉 Niveau {result.levelInfo.level} atteint !</p>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-800 overflow-hidden mb-5">
          <div className="px-4 py-2.5 border-b border-zinc-800">
            <span className="text-white font-black text-xs uppercase tracking-wider">Détail des manches</span>
          </div>
          <div className="divide-y divide-zinc-800/50">
            {result.details.map((d, i) => (
              <div key={i} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-zinc-400 text-xs">P{d.pos1} {d.name1} ↔ P{d.pos2} {d.name2}</span>
                  <PtsChip pts={d.pts} />
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="text-zinc-500">Réel : <span className="text-white font-bold">{fmt(d.actualSec)}</span></span>
                  <span className="text-zinc-500">Ta réponse : <span className="text-zinc-300">{fmt(d.guessSec)}</span></span>
                  {d.actualSec > 0 && (
                    <span className="text-zinc-600">{Math.round(Math.abs(d.actualSec - d.guessSec) / d.actualSec * 100)}% d'écart</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={loadRace}
            className="w-full py-3 rounded-xl font-black text-white text-sm"
            style={{ background: ACCENT }}>
            ⏱️ Nouvelle course
          </button>
          <Link to="/box-box" className="btn-secondary text-center">← Box Box Hub</Link>
        </div>
      </div>
    );
  }

  // ── PLAYING ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-5">
        <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: ACCENT }}>Le Gap</p>
        <h1 className="text-2xl font-black text-white leading-tight">Estime l'écart</h1>
        <p className="text-zinc-500 text-xs mt-0.5">
          {session.raceName} · Manche {currentRound + 1}/{TOTAL_ROUNDS}
          {raceCount > 0 && <span> · Session : <span className="text-white">+{sessionXp} XP</span></span>}
        </p>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-5">
        {Array.from({ length: TOTAL_ROUNDS }).map((_, i) => (
          <div key={i} className="h-1.5 rounded-full flex-1 transition-all"
            style={{ backgroundColor: i < session.guesses.length ? ACCENT : i === session.guesses.length ? ACCENT + '55' : '#27272a' }} />
        ))}
      </div>

      {/* Current duel */}
      {current && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 mb-5">
          <div className="grid grid-cols-3 items-center gap-2 mb-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full mb-1.5 font-black text-sm"
                style={{ background: ACCENT + '22', color: ACCENT }}>P{current.pos1}</div>
              <p className="text-white font-black text-sm leading-tight">{current.name1}</p>
              <p className="text-zinc-500 text-[10px] mt-0.5">{current.team1}</p>
            </div>
            <div className="text-center">
              <p className="text-zinc-600 text-2xl font-black">↔</p>
              <p className="text-zinc-600 text-[10px] mt-1">écart ?</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full mb-1.5 font-black text-sm"
                style={{ background: '#52525b44', color: '#a1a1aa' }}>P{current.pos2}</div>
              <p className="text-white font-black text-sm leading-tight">{current.name2}</p>
              <p className="text-zinc-500 text-[10px] mt-0.5">{current.team2}</p>
            </div>
          </div>

          <p className="text-zinc-400 text-xs text-center mb-3">Quel était l'écart entre ces deux pilotes ? (en secondes)</p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                step="0.1"
                min="0"
                value={session.input}
                onChange={e => setSession(p => ({ ...p, input: e.target.value }))}
                onKeyDown={handleKey}
                placeholder="ex: 4.5"
                autoFocus
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-500 transition-colors pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">s</span>
            </div>
            <button
              onClick={handleNext}
              disabled={!session.input || submitting}
              className="px-5 py-3 rounded-xl font-black text-sm text-white disabled:opacity-40 transition-all"
              style={{ background: session.input ? ACCENT : '#52525b' }}>
              {submitting ? '…' : isLastRound ? '✓ Fin' : '→'}
            </button>
          </div>
        </div>
      )}

      {/* Past rounds recap */}
      {session.guesses.length > 0 && (
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          <div className="px-4 py-2 border-b border-zinc-800">
            <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Manches précédentes</span>
          </div>
          <div className="divide-y divide-zinc-800/50">
            {session.guesses.map((g, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-zinc-400 text-xs">P{session.rounds[i].pos1} {session.rounds[i].name1} ↔ P{session.rounds[i].pos2} {session.rounds[i].name2}</span>
                <span className="text-zinc-300 text-xs font-bold">{fmt(g)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 text-center">
        <p className="text-zinc-700 text-[10px]">±10% → 100 pts · ±25% → 80 pts · ±50% → 60 pts · ±100% → 30 pts</p>
      </div>
    </div>
  );
};

export default LeGap;
